import {
  EmailTemplate,
  ListEmailTemplatesRequest,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/email-template/v1/emailtemplate.pb';
import { useQueryClient } from 'react-query';
import { EmailTemplateMutations, EmailTemplateQueries } from '@frontend/api-email-template';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { invalidateEmailTemplateLists } from './invalidate-email-template-lists';

export const useEmailTemplates = () => {
  const { t } = useTranslation('bulk-messaging');
  const alert = useAlert();
  const queryClient = useQueryClient();

  const getQueriesData = () =>
    // get all the list email template since there could be variations of the call (different filters, page sizes, collection, etc.)
    queryClient.getQueriesData<EmailTemplateMutations.PaginationData>(
      EmailTemplateQueries.keys.listEmailTemplatesBase()
    );

  const { mutateAsync: createTemplate } = EmailTemplateMutations.useCreateEmailTemplate({
    onError: () => {
      alert.error(t('There was an error creating this template. Please try again later.'));
    },
    onSuccess: async (res, req) => {
      const queriesData = getQueriesData();
      const newTemplate = { ...req, id: res?.templateId ?? '' };

      // update the cache to include the new template in the first pages's template in the list queries if he collection matches
      for (let i = 0; i < queriesData.length; i++) {
        const [keys, data] = queriesData[i];

        const requestKey = keys[2] as ListEmailTemplatesRequest; // 2nd index is the request object by convention

        if (!requestKey.collection || requestKey.collection !== newTemplate.collection) continue; // skip if the collection doesn't match

        // only prepend the new template to the first page
        const firstPage = data?.pages?.[0] ?? {};
        const newFirstPage = {
          ...firstPage,
          templates: [newTemplate, ...(firstPage.templates ?? [])],
        };

        // keep the rest of the pages the same
        const remainingPages = data?.pages?.slice(1) ?? [];

        const newData = {
          ...data,
          pages: [newFirstPage, ...remainingPages],
        };

        await queryClient.setQueryData(keys, newData);
      }

      invalidateEmailTemplateLists(queryClient);
      alert.success(t('Created template successfully.'));
    },
  });

  const { mutate: deleteTemplate } = EmailTemplateMutations.useDeleteEmailTemplate({
    onSettled: (_res, err) => {
      if (err) {
        alert.error(t('There was an error deleting this template. Please try again later.'));
      } else {
        alert.success({
          message: t('Deleted template successfully.'),
          // TODO: waiting for the BE to support undo
          // action: {
          //   label: t('Undo'),
          //   onClick: () => {
          //     // call undo delete endpoint
          //   },
          // },
        });
      }
    },
  });

  const fetchOrgTemplate = async (templateId?: string) => {
    const request = { templateId: templateId ?? '' };
    const data = await queryClient.fetchQuery(EmailTemplateQueries.keys.getEmailTemplate(request), {
      queryFn: () => EmailTemplateQueries.getEmailTemplate(request),
      retry: false,
    });
    // templates are either fetched or pulled from the cache (i.e. GetEmailTemplateResponse vs EmailTemplate format respectively)
    return data && 'template' in data ? data.template : (data as EmailTemplate | undefined);
  };

  const fetchSystemTemplate = async (templateId?: string) => {
    const request = { templateId: templateId ?? '' };
    const data = await queryClient.fetchQuery(EmailTemplateQueries.keys.getSystemEmailTemplate(request), {
      queryFn: () => EmailTemplateQueries.getSystemEmailTemplate(request),
      retry: false,
    });
    return data?.template;
  };

  const { mutate: updateTemplate } = EmailTemplateMutations.useUpdateEmailTemplate({
    onError: () => {
      alert.error(t('There was an error updating this template. Please try again later.'));
    },
    onSuccess: async (res, req) => {
      // get all the list email template since there could be variations of the call (different filters, page sizes, collection, etc.)
      const queriesData = getQueriesData();

      // update the cache to include the updated template in the list queries
      for (let i = 0; i < queriesData.length; i++) {
        const [keys, data] = queriesData[i];

        const newData = {
          ...data,
          pages:
            data?.pages.map((page) => ({
              ...page,
              // update the template in the list if it exists
              templates: (page.templates ?? []).map((template) =>
                template.id === req.templateId ? { ...template, ...res } : template
              ),
            })) ?? [],
        };

        await queryClient.setQueryData(keys, newData);
      }

      invalidateEmailTemplateLists(queryClient);
      alert.success(t('Updated template successfully.'));
    },
  });

  const { mutate: updateTemplateLocations } = EmailTemplateMutations.useUpdateEmailTemplateLocations({
    onSettled: (_res, err) => {
      if (err) {
        alert.error(t("There was an error updating this template's locations. Please try again later."));
      } else {
        invalidateEmailTemplateLists(queryClient);
        alert.success(t("Updated template's location successfully."));
      }
    },
  });

  return {
    createTemplate,
    deleteTemplate,
    fetchOrgTemplate,
    fetchSystemTemplate,
    updateTemplate,
    updateTemplateLocations,
  };
};
